import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Close,
  CameraAlt,
  PhotoLibrary,
  PlayArrow,
  Stop,
  Edit,
  History,
  Person,
  Schedule,
  LocationOn,
  Build
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from './api';

const WorkOrderDetailsDialog = ({ open, onClose, workOrder, onSuccess, userId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [editMode, setEditMode] = useState(false);

  // Form data for updates
  const [updateData, setUpdateData] = useState({
    notes: '',
    actualHours: '',
    completionNotes: '',
    title: '',
    description: '',
    location: '',
    equipment: '',
    priority: '',
    estimatedHours: ''
  });

  useEffect(() => {
    if (open && workOrder) {
      loadWorkOrderDetails();
      setActiveTab(0);
      setError(null);
      setEditMode(false);
    }
  }, [open, workOrder]);

  const loadWorkOrderDetails = async () => {
    try {
      setLoading(true);

      // Load work order details
      const details = await api.workOrders.get(workOrder.workOrderID);
      setWorkOrderDetails(details);

      // Initialize update data with current values
      setUpdateData({
        notes: '',
        actualHours: details.actualHours || '',
        completionNotes: '',
        title: details.title || '',
        description: details.description || '',
        location: details.location || '',
        equipment: details.equipment || '',
        priority: details.priority || '',
        estimatedHours: details.estimatedHours || ''
      });

      // Load history
      try {
        const historyData = await api.workOrders.getHistory(workOrder.workOrderID);
        setHistory(Array.isArray(historyData) ? historyData : []);
      } catch (err) {
        console.warn('History not available:', err);
        setHistory([]);
      }

      // Initialize photos array with existing attachments
      const existingPhotos = [];
      if (details.externalReference && details.externalReference.startsWith('/uploads/')) {
        existingPhotos.push({
          id: 'existing-photo',
          dataURL: `https://app-rmmsapi.azurewebsites.net${details.externalReference}`,
          timestamp: details.lastModifiedDate || details.creationDate,
          workOrderId: workOrder.workOrderID,
          isExisting: true
        });
      }
      setPhotos(existingPhotos);

    } catch (err) {
      console.error('Failed to load work order details:', err);
      setError('İş emri detayları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeWorkOrder = async () => {
    setActionLoading(true);
    try {
      const username = userId || 'unknown';
      console.log('Current userId:', userId);
      console.log('Sending userId to backend:', username);

      // Send photo if available
      const photoData = photos.length > 0 ? photos[0].dataURL : null;
      await api.workOrders.takeWorkOrder(workOrder.workOrderID, username, photoData);

      setWorkOrderDetails({
        ...workOrderDetails,
        workOrderStatus: 'InProgress'
      });
      toast.success('İş emri alındı');
      loadWorkOrderDetails();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to take work order:', err);
      setError('İş emri alınamadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteWorkOrder = async () => {
    if (!updateData.completionNotes.trim()) {
      setError('Tamamlama notları gereklidir');
      return;
    }

    setActionLoading(true);
    try {
      await api.workOrders.closeWorkOrder(workOrder.workOrderID, {
        CompletionNotes: updateData.completionNotes,
        ActualHours: updateData.actualHours ? parseFloat(updateData.actualHours) : null,
        Base64Photo: photos.length > 0 ? photos[0].dataURL : null
      });

      setWorkOrderDetails({
        ...workOrderDetails,
        workOrderStatus: 'Completed'
      });

      toast.success('İş emri tamamlandı');
      loadWorkOrderDetails();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to complete work order:', err);
      setError('İş emri tamamlanamadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    files.forEach(file => {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPhoto = {
            id: Date.now() + Math.random(),
            dataURL: e.target.result,
            timestamp: new Date().toISOString(),
            workOrderId: workOrder.workOrderID
          };

          setPhotos(prev => [...prev, newPhoto]);
        };
        reader.readAsDataURL(file);
      }
    });

    event.target.value = '';
  };

  const removePhoto = (photoId) => {
    // Don't remove existing photos, only newly added ones
    setPhotos(photos.filter(p => p.id !== photoId || p.isExisting));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'primary';
      case 'inprogress': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const handleClose = () => {
    setPhotos([]);
    setUpdateData({
      notes: '',
      actualHours: '',
      completionNotes: '',
      title: '',
      description: '',
      location: '',
      equipment: '',
      priority: '',
      estimatedHours: ''
    });
    setError(null);
    setEditMode(false);
    if (onClose) onClose();
  };

  const handleSaveChanges = async () => {
    setActionLoading(true);
    try {
      // API call to update work order details
      await api.workOrders.update(workOrder.workOrderID, {
        title: updateData.title,
        description: updateData.description,
        location: updateData.location,
        equipment: updateData.equipment,
        priority: updateData.priority,
        estimatedHours: updateData.estimatedHours ? parseFloat(updateData.estimatedHours) : null
      });

      toast.success('İş emri güncellendi');
      loadWorkOrderDetails();
      setEditMode(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to update work order:', err);
      setError('İş emri güncellenemedi');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const currentWorkOrder = workOrderDetails || workOrder;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            İş Emri #{currentWorkOrder?.workOrderID}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Detaylar" icon={<Edit />} />
            <Tab label="Geçmiş" icon={<History />} />
          </Tabs>
          {activeTab === 0 && currentWorkOrder?.workOrderStatus !== 'Completed' && (
            <Button
              variant={editMode ? "contained" : "outlined"}
              onClick={() => setEditMode(!editMode)}
              startIcon={<Edit />}
              size="small"
            >
              {editMode ? 'Düzenlemeyi İptal Et' : 'Düzenle'}
            </Button>
          )}
        </Box>

        {/* Details Tab */}
        {activeTab === 0 && (
          <Box>
            {/* Work Order Info Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  {editMode ? (
                    <TextField
                      label="Başlık"
                      value={updateData.title}
                      onChange={(e) => setUpdateData({...updateData, title: e.target.value})}
                      size="small"
                      sx={{ flexGrow: 1, mr: 2 }}
                    />
                  ) : (
                    <Typography variant="h6">
                      {currentWorkOrder?.title || `WO-${currentWorkOrder?.workOrderID}`}
                    </Typography>
                  )}
                  <Box display="flex" gap={1}>
                    <Chip
                      label={currentWorkOrder?.workOrderStatus || 'Bilinmiyor'}
                      color={getStatusColor(currentWorkOrder?.workOrderStatus)}
                      size="small"
                    />
                    {editMode ? (
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={updateData.priority}
                          onChange={(e) => setUpdateData({...updateData, priority: e.target.value})}
                          displayEmpty
                        >
                          <MenuItem value="">Yok</MenuItem>
                          <MenuItem value="Low">Düşük</MenuItem>
                          <MenuItem value="Medium">Orta</MenuItem>
                          <MenuItem value="High">Yüksek</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      currentWorkOrder?.priority && (
                        <Chip
                          label={currentWorkOrder?.priority}
                          color={getPriorityColor(currentWorkOrder?.priority)}
                          size="small"
                        />
                      )
                    )}
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {editMode ? (
                      <TextField
                        label="Açıklama"
                        value={updateData.description}
                        onChange={(e) => setUpdateData({...updateData, description: e.target.value})}
                        multiline
                        rows={2}
                        fullWidth
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Açıklama:</strong> {currentWorkOrder?.description || 'Açıklama yok'}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    {editMode ? (
                      <TextField
                        label="Lokasyon"
                        value={updateData.location}
                        onChange={(e) => setUpdateData({...updateData, location: e.target.value})}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <LocationOn fontSize="small" color="action" sx={{ mr: 1 }} />
                        }}
                      />
                    ) : (
                      currentWorkOrder?.location && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body2">{currentWorkOrder.location}</Typography>
                        </Box>
                      )
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    {editMode ? (
                      <TextField
                        label="Ekipman"
                        value={updateData.equipment}
                        onChange={(e) => setUpdateData({...updateData, equipment: e.target.value})}
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: <Build fontSize="small" color="action" sx={{ mr: 1 }} />
                        }}
                      />
                    ) : (
                      currentWorkOrder?.equipment && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Build fontSize="small" color="action" />
                          <Typography variant="body2">{currentWorkOrder.equipment}</Typography>
                        </Box>
                      )
                    )}
                  </Grid>

                  {currentWorkOrder?.assignedUserID && (
                    <Grid item xs={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">Atanan: {currentWorkOrder.assignedUserID}</Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule fontSize="small" color="action" />
                      <Typography variant="body2">Oluşturma: {formatDate(currentWorkOrder?.creationDate)}</Typography>
                    </Box>
                  </Grid>

                  {currentWorkOrder?.dueDate && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="warning.main">
                        <strong>Termin:</strong> {formatDate(currentWorkOrder.dueDate)}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={6}>
                    {editMode ? (
                      <TextField
                        label="Tahmini Süre (saat)"
                        value={updateData.estimatedHours}
                        onChange={(e) => setUpdateData({...updateData, estimatedHours: e.target.value})}
                        type="number"
                        inputProps={{ min: 0, step: 0.5 }}
                        fullWidth
                        size="small"
                      />
                    ) : (
                      currentWorkOrder?.estimatedHours && (
                        <Typography variant="body2">
                          <strong>Tahmini:</strong> {currentWorkOrder.estimatedHours} saat
                        </Typography>
                      )
                    )}
                  </Grid>

                  {currentWorkOrder?.actualHours && (
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Gerçekleşen:</strong> {currentWorkOrder.actualHours} saat
                      </Typography>
                    </Grid>
                  )}

                  {currentWorkOrder?.completionDate && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="success.main">
                        <strong>Tamamlanma:</strong> {formatDate(currentWorkOrder.completionDate)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Action Forms Based on Status */}
            {currentWorkOrder?.workOrderStatus === 'InProgress' && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>İş Emrini Tamamla</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="Tamamlama Notları *"
                      value={updateData.completionNotes}
                      onChange={(e) => setUpdateData({...updateData, completionNotes: e.target.value})}
                      multiline
                      rows={3}
                      fullWidth
                    />
                    <TextField
                      label="Gerçekleşen Süre (saat)"
                      value={updateData.actualHours}
                      onChange={(e) => setUpdateData({...updateData, actualHours: e.target.value})}
                      type="number"
                      inputProps={{ min: 0, step: 0.5 }}
                      fullWidth
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Notes Section - Always visible */}
            {(currentWorkOrder?.notes || editMode) && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Notlar</Typography>
                  {currentWorkOrder?.notes && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {currentWorkOrder.notes}
                    </Typography>
                  )}
                  {currentWorkOrder?.workOrderStatus !== 'Completed' && (
                    <TextField
                      label="Ek Not Ekle"
                      value={updateData.notes}
                      onChange={(e) => setUpdateData({...updateData, notes: e.target.value})}
                      multiline
                      rows={2}
                      fullWidth
                      size="small"
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Photos Section - Always visible at bottom of details */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Fotoğraflar ({photos.length})
                </Typography>

                <Box display="flex" gap={1} mb={2}>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibrary />}
                    onClick={() => fileInputRef.current?.click()}
                    size="small"
                  >
                    Galeriden Seç
                  </Button>
                </Box>

                {photos.length > 0 ? (
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {photos.map((photo) => (
                      <Box
                        key={photo.id}
                        sx={{
                          position: 'relative',
                          width: 120,
                          height: 120,
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={photo.dataURL}
                          alt="İş emri fotoğrafı"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                          }}
                          onClick={() => removePhoto(photo.id)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">Fotoğraf eklenmedi</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* History Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>İş Emri Geçmişi</Typography>
            {history.length > 0 ? (
              <List>
                {history.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem alignItems="flex-start">
                      <ListItemIcon>
                        <History color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<strong>{item.Action}</strong>}
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" color="text.secondary">
                              {item.Description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(item.ActionDate)} - Kullanıcı: {item.UserID}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < history.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography color="text.secondary">Geçmiş bilgisi yok</Typography>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={actionLoading}>
          Kapat
        </Button>

        {editMode && (
          <Button
            onClick={handleSaveChanges}
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            disabled={actionLoading}
            color="primary"
          >
            {actionLoading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </Button>
        )}

        {!editMode && currentWorkOrder?.workOrderStatus === 'Open' && (
          <Button
            onClick={handleTakeWorkOrder}
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={16} /> : <PlayArrow />}
            disabled={actionLoading}
            color="primary"
          >
            {actionLoading ? 'Alınıyor...' : 'İş Emrini Al'}
          </Button>
        )}

        {!editMode && currentWorkOrder?.workOrderStatus === 'InProgress' && (
          <Button
            onClick={handleCompleteWorkOrder}
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Stop />}
            disabled={actionLoading || !updateData.completionNotes.trim()}
            color="success"
          >
            {actionLoading ? 'Tamamlanıyor...' : 'İş Emrini Tamamla'}
          </Button>
        )}
      </DialogActions>

      {/* Hidden file input for gallery selection */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </Dialog>
  );
};

export default WorkOrderDetailsDialog;
